import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

// Currency formatter. INR groups the Indian way (₹1,10,000.00), matching the
// PDFs; everything else in thousands.
export function formatCurrency(
  amount: number | string,
  currency: string = 'USD',
  locale?: string,
): string {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return '$0.00';

  return new Intl.NumberFormat(locale ?? (currency === 'INR' ? 'en-IN' : 'en-US'), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

// Compact currency formatter (e.g., $1.2M)
export function formatCurrencyCompact(
  amount: number | string,
  currency: string = 'USD',
  locale: string = 'en-US',
): string {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return '$0';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numericAmount);
}

// Date formatters
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(parsed)) return '-';
  return format(parsed, 'MMM d, yyyy');
}

/**
 * Formats a calendar date that was stored as a UTC instant, reading it in UTC.
 *
 * Some values are date boundaries rather than moments: a payslip's period ends
 * at 23:59:59.999Z on the last day of the month, and a date of joining is
 * midnight UTC. Rendering those in local time shifts them a day — an August
 * payslip reads "Aug 1 – Sep 1" from IST, and a joining date of Jan 1 reads as
 * Dec 31 from any timezone behind UTC. Use this for date-only fields; keep
 * formatDate for genuine timestamps the user should see in their own timezone.
 */
export function formatDateUTC(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(parsed)) return '-';
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(parsed)) return '-';
  return format(parsed, 'MMM d, yyyy h:mm a');
}

export function formatDateShort(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(parsed)) return '-';
  return format(parsed, 'MM/dd/yyyy');
}

export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(parsed)) return '-';
  return formatDistanceToNow(parsed, { addSuffix: true });
}

export function formatDateForInput(date: string | Date | null | undefined): string {
  if (!date) return '';
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(parsed)) return '';
  return format(parsed, 'yyyy-MM-dd');
}

// Number formatters
export function formatNumber(
  value: number | string,
  locale: string = 'en-US',
): string {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numericValue)) return '0';
  return new Intl.NumberFormat(locale).format(numericValue);
}

export function formatCompactNumber(
  value: number | string,
  locale: string = 'en-US',
): string {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numericValue)) return '0';
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numericValue);
}

// Percentage formatter
export function formatPercentage(
  value: number | string,
  decimals: number = 1,
): string {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numericValue)) return '0%';
  return `${numericValue.toFixed(decimals)}%`;
}

// File size formatter
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Full name formatter
export function formatFullName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

// Initials formatter
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}
